import axios from "axios";
import Cookies from "js-cookie";

// hrSystemServer API base URL
const HR_API_BASE_URL = "https://api.oneplace.hr";

/**
 * Get authorization headers with bearer token
 */
const getAuthHeaders = () => {
  const token = Cookies.get("chatToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Profile API service for interacting with hrSystemServer candidate endpoints
 */
const profileApi = {
  /**
   * Get current user's profile using JWT token
   * Works for both phone-based auth and Telegram auth
   * @returns {Promise<Object>} Profile response
   */
  getMyProfile: async () => {
    try {
      const response = await axios.get(`${HR_API_BASE_URL}/v1/chat/profile/me`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }
  },

  /**
   * Get profile by phone number (legacy, use getMyProfile instead)
   * @param {string} phone - Phone number (stored without country code prefix in token)
   * @returns {Promise<Object>} Profile response
   */
  getProfileByPhone: async (phone) => {
    try {
      const response = await axios.get(`${HR_API_BASE_URL}/v1/chat/profile/by-phone`, {
        params: { phone },
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }
  },

  /**
   * Create or update profile
   * @param {Object} profileData - Profile data to create/update
   * @returns {Promise<Object>} Created/updated profile response
   */
  createOrUpdateProfile: async (profileData) => {
    try {
      const response = await axios.post(`${HR_API_BASE_URL}/v1/chat/profile`, profileData, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error creating/updating profile:", error);
      throw error;
    }
  },

  /**
   * Update profile (partial update) - LEGACY: requires phone
   * @param {Object} profileData - Profile fields to update
   * @returns {Promise<Object>} Updated profile response
   */
  updateProfile: async (profileData) => {
    try {
      const response = await axios.put(`${HR_API_BASE_URL}/v1/chat/profile`, profileData, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  },

  /**
   * Update current user's profile using JWT auth
   * Preferred for Telegram users who may not have phone
   * @param {Object} profileData - Profile fields to update (phone not required)
   * @returns {Promise<Object>} Updated profile response
   */
  updateMyProfile: async (profileData) => {
    try {
      const response = await axios.put(`${HR_API_BASE_URL}/v1/chat/profile/me`, profileData, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error updating my profile:", error);
      throw error;
    }
  },

  /**
   * Upload profile photo
   * @param {string} phone - Phone number
   * @param {File} file - Image file to upload
   * @returns {Promise<Object>} Updated profile response with photoUrl
   */
  uploadProfilePhoto: async (phone, file) => {
    try {
      const formData = new FormData();
      formData.append("phone", phone);
      formData.append("file", file);

      const response = await axios.post(`${HR_API_BASE_URL}/v1/chat/profile/photo`, formData, {
        headers: {
          ...getAuthHeaders(),
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading profile photo:", error);
      throw error;
    }
  },

  /**
   * Extract phone number from JWT token stored in cookie
   * Handles different phone formats from different auth providers
   * IMPORTANT: Does NOT use 'sub' claim as it may contain telegramId for Telegram users
   * @returns {string|null} Normalized phone number or null if not found
   */
  getPhoneFromToken: () => {
    const token = Cookies.get("chatToken");
    if (!token) return null;

    try {
      // Decode JWT payload (base64)
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));

      // Only use explicit phone claims - NOT 'sub' (which is telegramId for Telegram users)
      let phone = decoded.phone || decoded.phoneNumber || null;

      if (!phone) return null;

      // Remove any non-digit characters
      const digits = phone.replace(/\D/g, "");

      // Normalize phone number based on length
      // Mongolia: 8 digits -> add 976
      // Uzbekistan: 9 digits -> add 998
      // Kazakhstan: 10 digits -> add 7
      if (digits.length === 8) {
        return "976" + digits; // Mongolia
      } else if (digits.length === 9) {
        return "998" + digits; // Uzbekistan
      } else if (digits.length === 10) {
        return "7" + digits; // Kazakhstan
      } else if (digits.length === 11 && digits.startsWith("976")) {
        return digits; // Mongolia with country code
      } else if (digits.length === 12 && digits.startsWith("998")) {
        return digits; // Uzbekistan with country code
      } else if (digits.length === 11 && digits.startsWith("7")) {
        return digits; // Kazakhstan with country code
      }

      // Return as-is if already normalized or unknown format
      return digits;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  },

  /**
   * Extract telegramId from JWT token stored in cookie
   * @returns {string|null} Telegram ID or null if not found
   */
  getTelegramIdFromToken: () => {
    const token = Cookies.get("chatToken");
    if (!token) return null;

    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.telegramId || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  },

  /**
   * Get user identifier from token (phone or telegramId)
   * @returns {Object} { phone, telegramId, userId }
   */
  getUserIdentifier: () => {
    const token = Cookies.get("chatToken");
    if (!token) return { phone: null, telegramId: null, userId: null };

    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return {
        phone: decoded.phone || decoded.phoneNumber || null,
        telegramId: decoded.telegramId || null,
        userId: decoded.id || null,
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return { phone: null, telegramId: null, userId: null };
    }
  },
};

export default profileApi;
